import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import PhaseBadge from '../components/PhaseBadge';
import ProgramModal from '../components/ProgramModal';
import PasswordGate from '../../components/PasswordGate';
import { getEditPassword, authHeaders } from '../../services/auth';

function ProgramDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [program,  setProgram]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState(() => !!getEditPassword());
  const [showUnlockGate, setShowUnlockGate] = useState(false);

  const fetchProgram = useCallback(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/programs/${id}`)
      .then(res => setProgram(res.data))
      .catch(() => setError('Failed to load program details.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchProgram(); }, [fetchProgram]);

  if (loading) return <div className="loading-state">Loading program…</div>;
  if (error)   return <div className="error-state">{error}</div>;
  if (!program) return null;

  const companyId   = program.company_id || program.company?.id;
  const companyName = program.company_name || program.company?.name || 'Company';
  const brandName   = program.brand_name ||
    (program.drug_names || []).find(d => (d.name_type || d.type) === 'brand')?.name;

  const fields = [
    { label: 'Drug Name',        value: program.drug_name },
    { label: 'Brand Name',       value: brandName },
    { label: 'Company',          value: companyName },
    { label: 'Indication(s)', value: (program.indications && program.indications.length > 0)
        ? program.indications.map(i => i.name).join(' · ')
        : (program.indication_name || program.indication?.name) },
    { label: 'Phase',            value: <PhaseBadge phase={program.current_phase || program.trial_phase || program.phase} /> },
    { label: 'Status',           value: program.status },
    { label: 'Modality',         value: program.modality },
    { label: 'Target',           value: [program.target, ...(program.secondary_targets || [])].filter(Boolean).join(' · ') || null },
    { label: 'Approval Date',    value: program.approval_date },
    { label: 'Notes',            value: program.notes },
  ];

  return (
    <div>
      {editOpen && (
        <ProgramModal
          program={program}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); fetchProgram(); }}
          onDeleted={() => navigate('/pipeline')}
        />
      )}

      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <Link to="/pipeline" className="back-link">&larr; Back to Pipeline</Link>
          <h1 className="page-title">{program.drug_name || 'Program Detail'}</h1>
          {brandName && <p className="page-subtitle">{brandName}</p>}
        </div>
        {editMode ? (
          <button className="btn-secondary" style={{ marginBottom: 2 }} onClick={() => setEditOpen(true)}>
            Edit Program
          </button>
        ) : showUnlockGate ? (
          <PasswordGate onUnlocked={() => { setEditMode(true); setShowUnlockGate(false); }} />
        ) : (
          <button className="btn-secondary" style={{ marginBottom: 2 }} onClick={() => setShowUnlockGate(true)}>
            🔒 Unlock to edit
          </button>
        )}
      </div>

      <div className="page-body">

        {/* Overview */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="section-title">Program Overview</div>
          <div className="detail-grid">
            {fields.map(({ label, value }) => (
              <div className="detail-field" key={label}>
                <div className="detail-label">{label}</div>
                <div className="detail-value">
                  {value != null && value !== '' ? value : <span className="muted">—</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {companyId && (
          <div style={{ marginBottom: 24 }}>
            <Link to={`/pipeline/companies/${companyId}`} className="back-link">
              View {companyName} company profile &rarr;
            </Link>
          </div>
        )}

        {/* Linked Trials */}
        <LinkedTrialsCard programId={id} editMode={editMode} onTrialsChanged={fetchProgram} />

        {/* Chemical Structure (small molecules only) */}
        {program.modality === 'small_molecule' && (
          <StructureCard programId={id} hasSmiles={!!program.smiles} editMode={editMode} />
        )}

        {/* Aliases */}
        <AliasesCard aliases={(program.drug_names || program.aliases || []).filter(d => {
          const t = typeof d === 'object' ? (d.name_type || d.type) : null;
          return t !== 'inn' && t !== 'brand';
        })} />

        {/* Indication suggestions from trials */}
        <IndicationSuggestCard programId={id} currentIndications={program.indications || []} onAdded={fetchProgram} editMode={editMode} />

        {/* Milestones */}
        <MilestonesCard programId={id} initialMilestones={program.milestones || []} editMode={editMode} />

        {/* Linked EDGAR Filings */}
        <LinkedFilingsCard programId={id} editMode={editMode} />

      </div>
    </div>
  );
}

/* ── Indication suggestions from linked trials ───────────────────────── */
function IndicationSuggestCard({ programId, currentIndications, onAdded, editMode }) {
  const [suggestions, setSuggestions] = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [selected,    setSelected]    = useState(new Set());

  async function load() {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/programs/${programId}/suggest-indications`);
      const newOnly = (res.data.suggestions || []).filter(s => !s.already_linked);
      setSuggestions(newOnly);
      setSelected(new Set(newOnly.map((_, i) => i)));
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }

  async function addSelected() {
    setSaving(true);
    try {
      const toAdd = suggestions.filter((_, i) => selected.has(i));

      const resolvedIds = await Promise.all(toAdd.map(async s => {
        if (!s.is_new) return s.id;
        const res = await axios.post(`${API_BASE_URL}/indications`, { name: s.name }, { headers: authHeaders() });
        return res.data.id;
      }));

      const currentIds = currentIndications.map(i => i.id);
      const newIds = [...new Set([...currentIds, ...resolvedIds])];
      await axios.patch(`${API_BASE_URL}/programs/${programId}`, { indication_ids: newIds }, { headers: authHeaders() });
      onAdded();
      setSuggestions(null);
      setSelected(new Set());
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to add indications.');
    } finally {
      setSaving(false);
    }
  }

  function toggle(i) {
    setSelected(prev => { const next = new Set(prev); next.has(i) ? next.delete(i) : next.add(i); return next; });
  }

  if (!editMode) return null;

  if (suggestions === null) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Suggest Indications from Trials</span>
          <button className="btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={load} disabled={loading}>
            {loading ? 'Scanning…' : 'Scan linked trials'}
          </button>
        </div>
        <p style={{ padding: '10px 18px', color: '#94a3b8', fontSize: 13 }}>
          Click to scan trial conditions and suggest indications to add.
        </p>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="section-title">Suggest Indications from Trials</div>
        <p style={{ padding: '10px 18px', color: '#94a3b8', fontSize: 13 }}>
          No new indications found from linked trials.
        </p>
      </div>
    );
  }

  const allSelected = suggestions.every((_, i) => selected.has(i));

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Suggested Indications from Trials ({suggestions.length})</span>
        <button
          className="btn-secondary"
          style={{ fontSize: 11, padding: '2px 8px' }}
          onClick={() => setSelected(allSelected ? new Set() : new Set(suggestions.map((_, i) => i)))}
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>
      <div style={{ padding: '10px 18px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {suggestions.map((s, i) => (
            <label key={i} className="trial-suggestion-row">
              <input
                type="checkbox"
                checked={selected.has(i)}
                onChange={() => toggle(i)}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{s.name}</span>
                  {s.is_new && (
                    <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 6px' }}>
                      NEW
                    </span>
                  )}
                </div>
                {!s.is_new && (
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    Matched from: {s.matched_conditions.join(', ')}
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-secondary" onClick={() => setSuggestions(null)}>Dismiss</button>
          <button className="btn-primary" onClick={addSelected} disabled={saving || selected.size === 0}>
            {saving ? 'Adding…' : `Add ${selected.size} indication${selected.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Chemical Structure ──────────────────────────────────────────────── */
function StructureCard({ programId, hasSmiles, editMode }) {
  const [fetching, setFetching] = useState(false);
  const [smiles,   setSmiles]   = useState(hasSmiles);
  const [error,    setError]    = useState(null);
  const [imgKey,   setImgKey]   = useState(0);

  async function fetchStructure() {
    setFetching(true);
    setError(null);
    try {
      await axios.post(`${API_BASE_URL}/programs/${programId}/fetch-structure`, {}, { headers: authHeaders() });
      setSmiles(true);
      setImgKey(k => k + 1);
    } catch (e) {
      setError(e.response?.data?.error || 'PubChem lookup failed.');
    } finally {
      setFetching(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Chemical Structure</span>
        {editMode && (
          <button
            className="btn-secondary"
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={fetchStructure}
            disabled={fetching}
          >
            {fetching ? 'Fetching…' : smiles ? 'Refresh from PubChem' : 'Fetch from PubChem'}
          </button>
        )}
      </div>
      <div style={{ padding: '16px 18px', textAlign: 'center' }}>
        {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{error}</div>}
        {smiles ? (
          <img
            key={imgKey}
            src={`${API_BASE_URL}/programs/${programId}/structure.png?width=500&height=300`}
            alt="Chemical structure"
            style={{ maxWidth: '100%', borderRadius: 6 }}
          />
        ) : (
          <p style={{ color: '#94a3b8', fontSize: 13 }}>
            No structure stored yet. Click "Fetch from PubChem" to look up by drug name.
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Aliases (read-only) ─────────────────────────────────────────────── */
function AliasesCard({ aliases }) {
  const normalized = aliases.map(a => (typeof a === 'string' ? a : a.alias || a.name));
  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="section-title">Drug Name Aliases</div>
      {normalized.length === 0 ? (
        <p style={{ padding: '14px 18px', color: '#94a3b8', fontSize: 13 }}>No aliases recorded.</p>
      ) : (
        <div className="alias-tags">
          {normalized.map((alias, i) => (
            <span className="alias-tag" key={i}>{alias}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Milestones ──────────────────────────────────────────────────────── */
function MilestonesCard({ programId, initialMilestones, editMode }) {
  const [milestones, setMilestones] = useState(initialMilestones);
  const [showAdd,    setShowAdd]    = useState(false);
  const [addForm,    setAddForm]    = useState({ milestone_type: '', expected_date: '', actual_date: '', notes: '' });
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState(null);

  async function handleAdd() {
    if (!addForm.milestone_type.trim()) { setErr('Milestone type is required.'); return; }
    setSaving(true); setErr(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/programs/${programId}/milestones`, addForm, { headers: authHeaders() });
      const newMilestone = { ...addForm, id: res.data.id };
      setMilestones(prev => [...prev, newMilestone]);
      setAddForm({ milestone_type: '', expected_date: '', actual_date: '', notes: '' });
      setShowAdd(false);
    } catch {
      setErr('Failed to add milestone.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(milestoneId) {
    if (!window.confirm('Delete this milestone?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/milestones/${milestoneId}`, { headers: authHeaders() });
      setMilestones(prev => prev.filter(m => m.id !== milestoneId));
    } catch {
      alert('Failed to delete milestone.');
    }
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Development Milestones</span>
        {editMode && (
          <button
            className="btn-secondary"
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => setShowAdd(v => !v)}
          >
            {showAdd ? 'Cancel' : '+ Add'}
          </button>
        )}
      </div>

      {showAdd && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          {err && <div className="form-error" style={{ marginBottom: 8 }}>{err}</div>}
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div className="form-field">
              <label>Milestone <span className="required-star">*</span></label>
              <input
                type="text"
                value={addForm.milestone_type}
                onChange={e => setAddForm(f => ({ ...f, milestone_type: e.target.value }))}
                placeholder="e.g. Phase 2 readout"
                list="milestone-type-list"
                autoFocus
              />
              <datalist id="milestone-type-list">
                {['Phase 1 start', 'Phase 1 complete', 'Phase 2 start', 'Phase 2 readout',
                  'Phase 3 start', 'Phase 3 readout', 'NDA/BLA filing', 'FDA approval',
                  'IND filing', 'POC', 'Partnership deal'].map(m => (
                  <option key={m} value={m} />
                ))}
              </datalist>
            </div>
            <div className="form-field">
              <label>Expected Date</label>
              <input
                type="date"
                value={addForm.expected_date}
                onChange={e => setAddForm(f => ({ ...f, expected_date: e.target.value }))}
              />
            </div>
            <div className="form-field">
              <label>Actual Date</label>
              <input
                type="date"
                value={addForm.actual_date}
                onChange={e => setAddForm(f => ({ ...f, actual_date: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-field" style={{ marginTop: 8 }}>
            <label>Notes</label>
            <input
              type="text"
              value={addForm.notes}
              onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Optional notes"
            />
          </div>
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-primary" style={{ fontSize: 13 }} onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving…' : 'Add Milestone'}
            </button>
          </div>
        </div>
      )}

      {milestones.length === 0 ? (
        <p style={{ padding: '14px 18px', color: '#94a3b8', fontSize: 13 }}>No milestones recorded.</p>
      ) : (
        <ul className="milestone-list" style={{ padding: '10px 18px' }}>
          {milestones.map((m, i) => (
            <li className="milestone-item" key={m.id || i}>
              <div className="milestone-dot" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                  {m.milestone_type || m.event || m.title || 'Milestone'}
                </div>
                {m.actual_date && (
                  <div className="milestone-date">Actual: {m.actual_date}</div>
                )}
                {!m.actual_date && m.expected_date && (
                  <div className="milestone-date" style={{ color: '#94a3b8' }}>Expected: {m.expected_date}</div>
                )}
                {m.notes && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{m.notes}</div>
                )}
              </div>
              {m.id && editMode && (
                <button
                  className="btn-danger-sm"
                  onClick={() => handleDelete(m.id)}
                  title="Delete milestone"
                >
                  &times;
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Linked Trials ───────────────────────────────────────────────────── */
function LinkedTrialsCard({ programId, editMode, onTrialsChanged }) {
  const [trials,         setTrials]         = useState(null);
  const [loadingTrials,  setLoadingTrials]  = useState(false);
  const [showSearch,     setShowSearch]     = useState(false);
  const [suggestions,    setSuggestions]    = useState(null);
  const [loadingSug,     setLoadingSug]     = useState(false);
  const [selected,       setSelected]       = useState(new Set());
  const [linking,        setLinking]        = useState(false);
  const [nctInput,       setNctInput]       = useState('');
  const [manualLinking,  setManualLinking]  = useState(false);

  const loadTrials = useCallback((force = false) => {
    setTrials(prev => { if (prev === null) setLoadingTrials(true); return prev; });
    axios.get(`${API_BASE_URL}/programs/${programId}/suggest-trials`)
      .then(res => {
        const linked = res.data.linked_trials || [];
        setTrials(prev => {
          if (prev === null || force || linked.length >= (prev || []).length) return linked;
          return prev;
        });
        setSuggestions(null);
      })
      .catch(() => setTrials(prev => prev ?? []))
      .finally(() => setLoadingTrials(false));
  }, [programId]);

  useEffect(() => { loadTrials(); }, [loadTrials]);

  async function openSearch() {
    setShowSearch(true);
    if (suggestions) return;
    setLoadingSug(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/programs/${programId}/suggest-trials`);
      const linkedNcts = new Set((res.data.linked_trials || []).map(t => t.nct_id));
      const localUnlinked = (res.data.local_trials || []).filter(t => !linkedNcts.has(t.nct_id));
      const live = (res.data.live_trials || []).filter(t => !linkedNcts.has(t.nct_id));
      setSuggestions({ local_trials: localUnlinked, live_trials: live });
    } catch {
      setSuggestions({ local_trials: [], live_trials: [], linked_ids: [] });
    } finally {
      setLoadingSug(false);
    }
  }

  function closeSearch() {
    setShowSearch(false);
    setSelected(new Set());
  }

  function toggleTrial(nct_id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(nct_id)) next.delete(nct_id); else next.add(nct_id);
      return next;
    });
  }

  async function linkSelected() {
    setLinking(true);
    try {
      const allSuggested = [
        ...(suggestions?.local_trials || []),
        ...(suggestions?.live_trials  || []),
      ];

      const results = await Promise.all(
        [...selected].map(async nct_id => {
          try {
            const res = await axios.post(`${API_BASE_URL}/programs/${programId}/trials`, { nct_id }, { headers: authHeaders() });
            return { nct_id, trial_id: res.data.trial_id, ok: true };
          } catch {
            return { nct_id, trial_id: null, ok: false };
          }
        })
      );

      const newTrials = results
        .filter(r => r.ok)
        .map(r => {
          const src = allSuggested.find(t => t.nct_id === r.nct_id) || {};
          return { ...src, nct_id: r.nct_id, id: r.trial_id };
        });

      setTrials(prev => {
        const existing = new Set((prev || []).map(t => t.nct_id));
        return [...(prev || []), ...newTrials.filter(t => !existing.has(t.nct_id))];
      });

      setSuggestions(null);
      closeSearch();
      loadTrials(true);
      if (onTrialsChanged) onTrialsChanged();
    } finally {
      setLinking(false);
    }
  }

  async function linkManual() {
    const nct = nctInput.trim().toUpperCase();
    if (!nct) return;
    setManualLinking(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/programs/${programId}/trials`, { nct_id: nct }, { headers: authHeaders() });
      const trial_id = res.data.trial_id;
      setNctInput('');
      setSuggestions(null);
      setTrials(prev => {
        if ((prev || []).some(t => t.nct_id === nct)) return prev;
        return [...(prev || []), { nct_id: nct, id: trial_id, title: '', status: '', phase: '' }];
      });
      loadTrials(true);
      if (onTrialsChanged) onTrialsChanged();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to link trial.');
    } finally {
      setManualLinking(false);
    }
  }

  async function unlinkTrial(nct_id, trialId) {
    if (!window.confirm(`Unlink ${nct_id}?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/programs/${programId}/trials/${trialId}`, { headers: authHeaders() });
      setTrials(prev => prev.filter(t => t.nct_id !== nct_id));
      if (onTrialsChanged) onTrialsChanged();
    } catch {
      alert('Failed to unlink trial.');
    }
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span>Linked Clinical Trials {trials !== null && `(${trials.length})`}</span>
        {editMode && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={showSearch ? closeSearch : openSearch}>
              {showSearch ? 'Close Search' : '+ Find & Link Trials'}
            </button>
          </div>
        )}
      </div>

      {editMode && (
        <div style={{ padding: '10px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={nctInput}
            onChange={e => setNctInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') linkManual(); }}
            placeholder="Enter NCT ID directly (e.g. NCT04345042)"
            style={{ flex: 1, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}
          />
          <button className="btn-secondary" onClick={linkManual} disabled={manualLinking || !nctInput.trim()}>
            {manualLinking ? 'Linking…' : 'Link'}
          </button>
        </div>
      )}

      {showSearch && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          {loadingSug ? (
            <div style={{ color: '#64748b', fontSize: 13 }}>Searching ClinicalTrials.gov…</div>
          ) : suggestions && (suggestions.local_trials.length + suggestions.live_trials.length) === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>No unlinked matching trials found.</p>
          ) : suggestions ? (
            <>
              {suggestions.local_trials.length > 0 && (
                <SuggestionGroup
                  title="In local database"
                  trials={suggestions.local_trials}
                  selected={selected}
                  onToggle={toggleTrial}
                  onSelectAll={(ncts, add) => setSelected(prev => { const next = new Set(prev); ncts.forEach(n => add ? next.add(n) : next.delete(n)); return next; })}
                />
              )}
              {suggestions.live_trials.length > 0 && (
                <SuggestionGroup
                  title="From ClinicalTrials.gov (will be imported)"
                  trials={suggestions.live_trials}
                  selected={selected}
                  onToggle={toggleTrial}
                  onSelectAll={(ncts, add) => setSelected(prev => { const next = new Set(prev); ncts.forEach(n => add ? next.add(n) : next.delete(n)); return next; })}
                />
              )}
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn-secondary" onClick={closeSearch}>Cancel</button>
                <button className="btn-primary" onClick={linkSelected} disabled={linking || selected.size === 0}>
                  {linking ? 'Linking…' : `Link ${selected.size} trial${selected.size !== 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {loadingTrials ? (
        <p style={{ padding: '14px 18px', color: '#94a3b8', fontSize: 13 }}>Loading…</p>
      ) : !trials || trials.length === 0 ? (
        <p style={{ padding: '14px 18px', color: '#94a3b8', fontSize: 13 }}>No clinical trials linked yet.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>NCT ID</th>
              <th>Title</th>
              <th>Phase</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {trials.map((t, i) => (
              <tr key={t.id || t.nct_id || i}>
                <td>
                  {t.nct_id ? (
                    <a
                      href={`https://clinicaltrials.gov/study/${t.nct_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ext-link"
                    >
                      {t.nct_id}
                    </a>
                  ) : '—'}
                </td>
                <td style={{ maxWidth: 360 }}>{t.title || t.brief_title || '—'}</td>
                <td><PhaseBadge phase={t.phase} /></td>
                <td>{t.status || t.overall_status || '—'}</td>
                <td>
                  {editMode && (
                    <button
                      className="btn-danger-sm"
                      onClick={() => unlinkTrial(t.nct_id, t.id)}
                      title="Unlink trial"
                    >
                      Unlink
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ── Linked EDGAR Filings ────────────────────────────────────────────── */
function LinkedFilingsCard({ programId, editMode }) {
  const [filings,       setFilings]      = useState(null);
  const [loadingInit,   setLoadingInit]  = useState(false);
  const [showSearch,    setShowSearch]   = useState(false);
  const [suggestions,   setSuggestions]  = useState(null);
  const [loadingSug,    setLoadingSug]   = useState(false);
  const [selected,      setSelected]     = useState(new Set());
  const [linking,       setLinking]      = useState(false);

  const loadFilings = useCallback(() => {
    setLoadingInit(true);
    axios.get(`${API_BASE_URL}/programs/${programId}/suggest-filings`)
      .then(res => {
        setFilings(res.data.linked_filings || []);
        setSuggestions(null);
      })
      .catch(() => setFilings([]))
      .finally(() => setLoadingInit(false));
  }, [programId]);

  useEffect(() => { loadFilings(); }, [loadFilings]);

  async function openSearch() {
    setShowSearch(true);
    if (suggestions) return;
    setLoadingSug(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/programs/${programId}/suggest-filings`);
      const linkedAccessions = new Set((res.data.linked_filings || []).map(f => f.accession_number));
      const linkedIds = new Set((res.data.linked_filings || []).map(f => f.id));
      setSuggestions({
        local_filings: (res.data.local_filings || []).filter(f => !linkedIds.has(f.id)),
        live_filings:  (res.data.live_filings || []).filter(f => !linkedAccessions.has(f.accession_number)),
      });
    } catch {
      setSuggestions({ local_filings: [], live_filings: [] });
    } finally {
      setLoadingSug(false);
    }
  }

  function closeSearch() { setShowSearch(false); setSelected(new Set()); }

  function toggleFiling(key) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function linkSelected() {
    setLinking(true);
    const allSug = [...(suggestions?.local_filings || []), ...(suggestions?.live_filings || [])];
    try {
      const results = await Promise.all(
        [...selected].map(async key => {
          const filing = allSug.find(f => (f.accession_number || String(f.id)) === key);
          if (!filing) return null;
          try {
            const payload = filing.id
              ? { filing_id: filing.id }
              : {
                  accession_number: filing.accession_number,
                  filing_type:      filing.filing_type,
                  filed_date:       filing.filed_date,
                  form_url:         filing.form_url,
                  company_name:     filing.company_name,
                };
            const res = await axios.post(`${API_BASE_URL}/programs/${programId}/filings`, payload, { headers: authHeaders() });
            return { ...filing, id: res.data.filing_id };
          } catch { return null; }
        })
      );
      const newFilings = results.filter(Boolean);
      setFilings(prev => {
        const existing = new Set((prev || []).map(f => f.id));
        return [...(prev || []), ...newFilings.filter(f => !existing.has(f.id))];
      });
      setSuggestions(null);
      closeSearch();
    } finally {
      setLinking(false);
    }
  }

  async function unlinkFiling(filingId) {
    if (!window.confirm('Unlink this filing?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/programs/${programId}/filings/${filingId}`, { headers: authHeaders() });
      setFilings(prev => prev.filter(f => f.id !== filingId));
    } catch { alert('Failed to unlink filing.'); }
  }

  const totalLocal  = (suggestions?.local_filings || []).length;
  const totalLive   = (suggestions?.live_filings || []).length;

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>EDGAR Filings {filings !== null && `(${filings.length} linked)`}</span>
        {editMode && (
          <button className="btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={showSearch ? closeSearch : openSearch}>
            {showSearch ? 'Close Search' : '+ Search EDGAR'}
          </button>
        )}
      </div>

      {showSearch && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          {loadingSug ? (
            <div style={{ color: '#64748b', fontSize: 13 }}>Searching EDGAR full-text…</div>
          ) : !suggestions ? null
          : (totalLocal + totalLive) === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>No unlinked matching filings found.</p>
          ) : (
            <>
              {totalLocal > 0 && (
                <FilingSuggestionGroup
                  title="In local database"
                  filings={suggestions.local_filings}
                  selected={selected}
                  onToggle={toggleFiling}
                  keyFn={f => String(f.id)}
                  onSelectAll={(keys, add) => setSelected(prev => { const next = new Set(prev); keys.forEach(k => add ? next.add(k) : next.delete(k)); return next; })}
                />
              )}
              {totalLive > 0 && (
                <FilingSuggestionGroup
                  title={`From EDGAR full-text search (${totalLive} found)`}
                  filings={suggestions.live_filings}
                  selected={selected}
                  onToggle={toggleFiling}
                  keyFn={f => f.accession_number}
                  onSelectAll={(keys, add) => setSelected(prev => { const next = new Set(prev); keys.forEach(k => add ? next.add(k) : next.delete(k)); return next; })}
                />
              )}
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn-secondary" onClick={closeSearch}>Cancel</button>
                <button className="btn-primary" onClick={linkSelected} disabled={linking || selected.size === 0}>
                  {linking ? 'Linking…' : `Link ${selected.size} filing${selected.size !== 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {loadingInit ? (
        <p style={{ padding: '14px 18px', color: '#94a3b8', fontSize: 13 }}>Loading…</p>
      ) : !filings || filings.length === 0 ? (
        <p style={{ padding: '14px 18px', color: '#94a3b8', fontSize: 13 }}>No EDGAR filings linked yet.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Company</th>
              <th>Filed</th>
              <th>Link</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filings.map((f, i) => (
              <tr key={f.id || i}>
                <td><strong>{f.filing_type || '—'}</strong></td>
                <td>{f.company_name || f.excerpt || '—'}</td>
                <td>{f.filed_date || '—'}</td>
                <td>
                  {(f.form_url || f.url) ? (
                    <a href={f.form_url || f.url} target="_blank" rel="noopener noreferrer" className="ext-link">
                      View &rarr;
                    </a>
                  ) : '—'}
                </td>
                <td>
                  {editMode && <button className="btn-danger-sm" onClick={() => unlinkFiling(f.id)}>Unlink</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function FilingSuggestionGroup({ title, filings, selected, onToggle, keyFn, onSelectAll }) {
  const keys = filings.map(keyFn);
  const allSelected = keys.length > 0 && keys.every(k => selected.has(k));
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#64748b', marginBottom: 4 }}>
        {title}
      </div>
      <button
        className="btn-secondary"
        style={{ fontSize: 11, padding: '2px 8px', marginBottom: 8 }}
        onClick={() => onSelectAll(keys, !allSelected)}
      >
        {allSelected ? 'Deselect all' : 'Select all'}
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filings.map(f => {
          const key = keyFn(f);
          return (
            <label key={key} className="trial-suggestion-row">
              <input type="checkbox" checked={selected.has(key)} onChange={() => onToggle(key)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', flexShrink: 0 }}>
                    {f.filing_type || 'Filing'}
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>{f.filed_date || ''}</span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.company_name || f.excerpt || f.accession_number || '—'}
                </div>
                {(f.file_description || f.biz_location) && (
                  <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.file_description || f.biz_location}
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function SuggestionGroup({ title, trials, selected, onToggle, onSelectAll }) {
  const allSelected = trials.every(t => selected.has(t.nct_id));
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#64748b', marginBottom: 4 }}>
        {title}
      </div>
      <button
        className="btn-secondary"
        style={{ fontSize: 11, padding: '2px 8px', marginBottom: 8 }}
        onClick={() => onSelectAll(trials.map(t => t.nct_id), !allSelected)}
      >
        {allSelected ? 'Deselect all' : 'Select all'}
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {trials.map(t => (
          <label key={t.nct_id} className="trial-suggestion-row">
            <input
              type="checkbox"
              checked={selected.has(t.nct_id)}
              onChange={() => onToggle(t.nct_id)}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', flexShrink: 0 }}>{t.nct_id}</span>
                {t.phase && <span style={{ fontSize: 11, color: '#3b82f6' }}>{t.phase}</span>}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.title || t.brief_title || '—'}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

export default ProgramDetail;
